import { useTestBlockSelection } from './TestBlockSelectionContext';
import type { MouseEvent } from 'react';

/** Thickness of the insertion rule, and the whole indicator's height when pending. */
const RULE_HEIGHT = 2;

interface BlockPasteGapProps {
    realId: string;
    position: 'before' | 'after';
}

/**
 * Paste-here target straddling a block edge. Only rendered while the user is
 * carrying blocks (clipboardState set). Absolutely positioned so it never
 * affects layout — pagination measurements stay untouched.
 */
export function BlockPasteGap({ realId, position }: BlockPasteGapProps) {
    const { enabled, clipboardState, pasteAt } = useTestBlockSelection();

    if (!enabled || !clipboardState) return null;

    const handleClick = (event: MouseEvent) => {
        event.stopPropagation();
        pasteAt(position === 'before' ? 'start' : { afterRealId: realId });
    };

    return (
        <div
            data-block-selection-ui
            onClick={handleClick}
            className={`group/gap absolute inset-x-0 z-20 flex h-7 cursor-pointer items-center ${
                position === 'before' ? '-top-8' : '-bottom-8'
            }`}
        >
            <div className="flex w-full items-center gap-2 px-1 opacity-0 transition-opacity duration-150 group-hover/gap:opacity-100">
                <div className="h-0.5 flex-1 rounded-full bg-blue-400" />
                <span className="whitespace-nowrap rounded-full border border-blue-200 bg-blue-50 px-3 py-0.5 text-xs font-medium text-blue-600 shadow-sm">
                    Colar aqui
                </span>
                <div className="h-0.5 flex-1 rounded-full bg-blue-400" />
            </div>
        </div>
    );
}

/**
 * Where the drag will land. Anchored flush to a block edge — `bottom:100%`
 * grows upward into the gap before the block, `top:100%` grows downward for
 * the append slot — and absolutely positioned + non-interactive, so it never
 * affects layout flow (pagination stays untouched) and never intercepts the
 * drop.
 *
 * Two states:
 * - **slot** (default): dnd has opened a real gap of `height`, which this
 *   fills exactly, capped by a rule on the insertion edge.
 * - **pending**: the cursor is between two pages, in nobody's droppable. The
 *   neighbours have snapped back, so there is no gap — only the rule is drawn,
 *   holding the last valid edge until the pointer re-enters a page.
 */
export function TestDropIndicator({
    position,
    height,
    pending = false,
}: {
    position: 'before' | 'after';
    height: number;
    pending?: boolean;
}) {
    return (
        <div
            aria-hidden
            data-block-selection-ui
            data-testid="test-drop-indicator"
            data-drop-state={pending ? 'pending' : 'slot'}
            className={`pointer-events-none absolute inset-x-0 z-30 rounded-lg ${
                pending ? '' : 'bg-slate-400/10'
            }`}
            style={{
                height: pending ? RULE_HEIGHT : height,
                ...(position === 'before' ? { bottom: '100%' } : { top: '100%' }),
            }}
        >
            <div
                className="absolute inset-x-0 top-0 rounded-full bg-blue-400"
                style={{ height: RULE_HEIGHT }}
            />
        </div>
    );
}
